import type { GTMEventReturnType } from './GTM.types';
import { hashEmail } from '../util/encrypt';

declare global {
    interface Window {
        dataLayer: (GTMEventReturnType | { ecommerce: null })[];
    }
}

enum MetaTagName {
    USER_HASH = 'user_hash',
    LOGIN_METHOD = 'login_method',
    USER_ID = 'user_id'
}

export default class GTM {
    public static clearEcommerce(): void {
        if (typeof window === 'undefined' || !window.dataLayer) {
            return;
        }

        window.dataLayer.push({ ecommerce: null });
    }

    public static track<DT extends GTMEventReturnType>(eventData: DT): void {
        if (typeof window === 'undefined' || !window.dataLayer) {
            return;
        }

        window.dataLayer.push(eventData);
    }

    public static trackEcommerce<DT extends GTMEventReturnType>(eventData: DT): void {
        if (typeof window === 'undefined' || !window.dataLayer) {
            return;
        }

        if ('ecommerce' in eventData) {
            GTM.clearEcommerce();
        }

        window.dataLayer.push(eventData);
    }

    public static async setMetaUserHash(email: string): Promise<void> {
        const emailHash = await hashEmail(email);
        this.#createMetaElement(MetaTagName.USER_HASH, emailHash);
    }

    public static setMetaLoginMethod(method: string): void {
        this.#createMetaElement(MetaTagName.LOGIN_METHOD, method);
    }

    public static setMetaUserId(userId: string): void {
        this.#createMetaElement(MetaTagName.USER_ID, userId);
    }

    static #createMetaElement(name: MetaTagName, content: string): void {
        let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

        if (!meta) {
            meta = document.createElement('meta');
            meta.name = name;
            document.head.appendChild(meta);
        }

        meta.setAttribute('content', content);
    }
}
